import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Check, Clock, Lock, Mail } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { fetchMe, loginUser } from "@/entities/auth/api";
import { ApiError } from "@/shared/api/http";
import { useAuthStore } from "@/shared/lib/auth-store";
import { Button } from "@/shared/ui/button";
import { AuthShell } from "@/widgets/AuthShell";

const schema = z.object({
  email: z.string().email("Введите корректный email"),
  password: z.string().min(1, "Введите пароль"),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [search] = useSearchParams();
  const justRegistered = search.get("registered") === "true";
  const justApproved = search.get("approved") === "true";
  const [pending, setPending] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const { token } = await loginUser(values);
      // достаём пользователя сразу, чтобы знать is_admin
      useAuthStore.setState({ token });
      const me = await fetchMe();
      return { token, me };
    },
    onSuccess: ({ token, me }) => {
      login(token, me);
      navigate("/", { replace: true });
    },
    onError: (err: unknown) => {
      setPending(false);
      setBlocked(false);
      if (err instanceof ApiError) {
        if (err.code === "PENDING_APPROVAL") setPending(true);
        else if (err.code === "BLOCKED") setBlocked(true);
        else if (err.code === "INVALID_CREDENTIALS") {
          form.setError("password", { message: "Неверный email или пароль" });
        } else {
          form.setError("password", { message: err.message });
        }
      } else {
        form.setError("password", { message: "Не удалось войти" });
      }
      // на случай — сбросим частично записанный токен
      useAuthStore.setState({ token: null, user: null });
      localStorage.removeItem("sc.token");
      localStorage.removeItem("sc.user");
    },
  });

  return (
    <AuthShell>
      <div className="glass-card p-8">
        {justRegistered && justApproved && (
          <Alert kind="success" className="mb-5">
            Регистрация принята, можете войти.
          </Alert>
        )}
        {pending && (
          <Alert kind="warn" className="mb-5">
            Ваш аккаунт ожидает подтверждения администратором. Если вы прошли курс — напишите автору в Telegram, и доступ дадут быстрее.
          </Alert>
        )}
        {blocked && (
          <Alert kind="error" className="mb-5">
            Аккаунт заблокирован. Свяжитесь с администратором.
          </Alert>
        )}

        <h2 className="text-2xl font-bold tracking-tight text-white">Вход</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Подбор семантики для блогеров, экспертов и авторов курсов.
        </p>

        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="mt-6 flex flex-col gap-4"
        >
          <Field
            label="Email"
            icon={<Mail className="h-4 w-4" />}
            placeholder="you@mail.com"
            type="email"
            autoComplete="email"
            error={form.formState.errors.email?.message}
            register={form.register("email")}
          />
          <Field
            label="Пароль"
            icon={<Lock className="h-4 w-4" />}
            placeholder="••••••••"
            type="password"
            autoComplete="current-password"
            error={form.formState.errors.password?.message}
            register={form.register("password")}
          />
          <Button
            type="submit"
            className="mt-1 w-full justify-center bg-gradient-primary text-white shadow-glow-purple hover:shadow-glow-purple-lg"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Вход…" : "Войти"} <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          Ещё нет аккаунта? <Link to="/register" className="font-semibold text-purple hover:underline">Создать</Link>
        </div>
      </div>
    </AuthShell>
  );
}

// ---- field + alert (локальные, не уходят в shared/ui) ----

function Field(props: {
  label: string;
  icon?: React.ReactNode;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  error?: string;
  register: ReturnType<typeof useForm<FormValues>>["register"] extends (...args: never[]) => infer R ? R : never;
}): JSX.Element {
  const { label, icon, error, register, ...rest } = props;
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-foreground/90">{label}</span>
      <div
        className={
          "flex items-center gap-2 rounded-lg border bg-white/[0.04] px-3 transition-colors focus-within:border-purple focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.15)]" +
          (error ? " border-pink/50" : " border-white/10")
        }
      >
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <input
          {...register}
          {...rest}
          className="h-10 w-full bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
        />
      </div>
      {error && <span className="text-xs text-pink">{error}</span>}
    </label>
  );
}

function Alert({
  kind,
  children,
  className,
}: {
  kind: "success" | "warn" | "info" | "error";
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  const map = {
    success: { color: "text-green", bg: "bg-green/8", border: "border-green/35", Icon: Check },
    info:    { color: "text-cyan",  bg: "bg-cyan/8",  border: "border-cyan/35",  Icon: Clock },
    warn:    { color: "text-amber", bg: "bg-amber/8", border: "border-amber/35", Icon: AlertTriangle },
    error:   { color: "text-pink",  bg: "bg-pink/8",  border: "border-pink/35",  Icon: AlertTriangle },
  } as const;
  const s = map[kind];
  const Icon = s.Icon;
  return (
    <div className={`flex items-start gap-2.5 rounded-lg border ${s.bg} ${s.border} p-3 text-sm leading-snug ${className ?? ""}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${s.color}`} />
      <div className="text-foreground/90">{children}</div>
    </div>
  );
}
